import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { FlaskConical, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApi } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, ErrorBox } from "@/components/common/ui-bits";
import { TemplateEditor } from "@/components/notifications/TemplateEditor";
import { EmailHistory } from "@/components/notifications/EmailHistory";
import { ConsentPanel, useConsents } from "@/components/notifications/ConsentPanel";
import { fmtDateTime } from "@/lib/format";

function TemplateCard({ t, onEdit, reload, email }) {
  const toggle = async (enabled) => {
    try { await api.put(`/email-templates/${t.key}`, { subject: t.subject, body: t.body, button_text: t.button_text || null, enabled }); toast.success(enabled ? "Envío automático activado" : "Envío automático desactivado"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const test = async () => {
    try { const r = await api.post(`/email-templates/${t.key}/test`); toast.success(`Prueba a ${email}: ${r.data.status}`); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="card-box fade-up flex flex-col gap-3 p-5" data-testid={`template-card-${t.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0"><p className="font-bold text-slate-900">{t.name} <span className={`ml-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${t.category === "Marketing" ? "bg-fuchsia-50 text-fuchsia-700" : "bg-slate-100 text-slate-600"}`} data-testid={`template-category-${t.key}`}>{t.category}</span></p><p className="text-xs text-slate-500">{t.event}</p></div>
        {t.category === "Marketing" ? <span className="shrink-0 text-xs text-slate-500">Se envía por campaña</span> : <label className="flex shrink-0 items-center gap-2 text-xs text-slate-600">{t.enabled ? "Activa" : "Desactivada"}
          <Switch checked={t.enabled} onCheckedChange={toggle} data-testid={`template-toggle-${t.key}`} aria-label={`Activar ${t.name}`} /></label>}
      </div>
      <p className="truncate rounded bg-slate-50 px-3 py-2 text-sm text-slate-700">{t.subject}</p>
      <div className="mt-auto flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => onEdit(t)} data-testid={`template-edit-${t.key}`}><Pencil className="mr-1.5 h-3.5 w-3.5" />Editar y vista previa</Button>
        <Button size="sm" variant="outline" onClick={test} data-testid={`template-test-${t.key}`}><FlaskConical className="mr-1.5 h-3.5 w-3.5" />Enviar prueba</Button>
        {t.updated_by && <span className="text-xs text-slate-400">Editada por {t.updated_by} · {fmtDateTime(t.updated_at)}</span>}
      </div>
    </div>
  );
}

export default function Notifications() {
  const { user } = useAuth();
  const { data, error, reload } = useApi("/email-templates");
  const [editing, setEditing] = useState(null);
  const consents = useConsents();
  const consented = consents.data?.clients.filter((c) => c.consent).length;
  return (
    <div>
      <PageHeader title="Correos del negocio" subtitle="Cada tipo de correo se envía automáticamente solo si está activado. Las cuentas demo (.test) nunca reciben correos reales." />
      <ErrorBox text={error} />
      <Tabs defaultValue="templates">
        <TabsList className="mb-5"><TabsTrigger value="templates" data-testid="tab-templates">Plantillas</TabsTrigger><TabsTrigger value="history" data-testid="tab-history">Historial de envíos</TabsTrigger><TabsTrigger value="consent" data-testid="tab-consent">Consentimientos</TabsTrigger></TabsList>
        <TabsContent value="templates">
          {!data ? <Loading /> : <div className="grid gap-4 md:grid-cols-2">{data.map((t) => <TemplateCard key={t.key} t={t} onEdit={setEditing} reload={reload} email={user.email} />)}</div>}
        </TabsContent>
        <TabsContent value="history"><EmailHistory templates={data} consented={consented} /></TabsContent>
        <TabsContent value="consent"><ConsentPanel data={consents.data} reload={consents.reload} /></TabsContent>
      </Tabs>
      <TemplateEditor tpl={editing} onClose={() => setEditing(null)} onSaved={reload} />
    </div>
  );
}
